import { useAssignInfo } from "@saleor/accountSettings/misc";
import ExpressFleetListPage from "@saleor/autobooking/components/ExpressFleetListPage";
import { useExpressFleetMallsConfig } from "@saleor/autobooking/queries";
import {
  ExpressFleetListUrlQueryParams,
  expressFleetUrl
} from "@saleor/autobooking/urls";
import { DEFAULT_INITIAL_PAGINATION_DATA } from "@saleor/config";
import { configurationOpsMenuUrl } from "@saleor/configuration_operations";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import usePaginator, {
  createPaginationState,
  PaginationState
} from "@saleor/hooks/usePaginator";
import useUser from "@saleor/hooks/useUser";
import { maybe } from "@saleor/misc";
import { ListViews } from "@saleor/types";
import { MallSortField, OrderDirection } from "@saleor/types/globalTypes";
import * as React from "react";

interface ExpressFleetListProps {
  params: ExpressFleetListUrlQueryParams;
}

const ExpressFleetList: React.FC<ExpressFleetListProps> = ({ params }) => {
  const navigate = useNavigator();
  const paginate = usePaginator();
  const user = useUser();
  const userType = user?.user?.userType?.code;
  const userInfo = useAssignInfo(user?.user);

  const [activeTab, setActiveTab] = React.useState("fnb");

  const { updateListSettings, settings } = useListSettings(ListViews.MALL_LIST);

  const sortBy = {
    sortBy: {
      direction: OrderDirection.ASC,
      field:
        activeTab === "fnb"
          ? MallSortField.FB_AUTOBOOKING_STATUS_NAME
          : MallSortField.GM_AUTOBOOKING_STATUS_NAME
    }
  };

  const [queryVariables, setQueryVariables] = React.useState<PaginationState>({
    first: settings.rowNumber
  });

  const { data, refetch, loading: isLoading } = useExpressFleetMallsConfig({
    displayLoader: true,
    variables: {
      ...queryVariables,
      ...sortBy,
      id: !userType ? null : [userInfo?.mall],
      isServiceable: true,
      search: params?.search || "",
      status: "ACT"
    }
  });

  const mallList = maybe(() => data.malls.edges.map(edges => edges.node));

  const paginationState =
    createPaginationState(settings.rowNumber, params)?.first !== null
      ? createPaginationState(settings.rowNumber, params)
      : { first: settings.rowNumber };

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    data?.malls?.pageInfo,
    paginationState,
    params
  );

  React.useEffect(() => {
    navigate(
      expressFleetUrl({
        ...params,
        ...DEFAULT_INITIAL_PAGINATION_DATA
      }),
      true
    );
  }, [settings.rowNumber]);

  React.useMemo(() => {
    setQueryVariables(paginationState);
  }, [params]);

  return (
    <ExpressFleetListPage
      params={params}
      refetch={refetch}
      data={data}
      mallList={mallList}
      isLoading={isLoading}
      settings={settings}
      onUpdateListSettings={updateListSettings}
      pageInfo={pageInfo}
      onPreviousPage={loadPreviousPage}
      brands={undefined}
      onAdd={null}
      disabled={false}
      onRowClick={null}
      onNextPage={loadNextPage}
      onBack={() => navigate(configurationOpsMenuUrl)}
      userType={userType}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
    />
  );
};

export default ExpressFleetList;
